/* screens/academy/landing/CourseLanding.jsx — рендер лендинга курса (#16).
   Props:
     payload  — ответ public-landing.json: {course, landing{enabled,blocks}, tariffs,
                program, my_enrollment}
     mode     — 'anon' | 'app' | 'preview'
   Поведение:
   - landing.enabled === false → дефолтный авто-лендинг из title/summary/description/
     program/tariffs (блоки hero+text+program+pricing+cta генерируем на лету).
   - CTA по состоянию (см. матрицу ниже). В режиме 'preview' все CTA ИНЕРТНЫ.
   Безопасность блоков (S5/S6) — в blocks.jsx (markdown через ChatMarkdown, картинки
   только http/https, рендерятся только allowlisted-поля). */

import React from 'react';
import { Button } from '../../../components/ui/Button.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { RR } from '../../../lib/router.jsx';
import * as api from '../../../lib/api/academy.js';
import { academyErrorText } from '../../../lib/academy/errorCodes.js';
import { isInviteOnly, isLandingAccessExpired, courseCtaLabel } from '../../../lib/academy/access.js';
import { renderBlock } from './blocks.jsx';
import { BuyDialog } from '../BuyDialog.jsx';

const { useState, useCallback } = React;
const { useNavigate } = RR;

// Дефолтный авто-лендинг: собираем безопасные блоки из публичных полей курса.
// description приходит как markdown (если бэк отдал) — рендерится через text-блок.
/* Курс «только по записи методистом» обязан иметь на странице форму заявки:
   самозапись у него закрыта гейтом бэка, и без формы посетителю физически некуда
   деться. Раньше кнопка вела в отказ с советом «оставьте заявку на странице
   курса» — на странице, где формы могло не быть вовсе. */
function ensureLeadForm(blocks, course) {
  if (!isInviteOnly(course)) return blocks;
  const hasForm = blocks.some((b) => b && (b.type === 'cta' || b.type === 'hero') && b.mode === 'form');
  if (hasForm) return blocks;
  return blocks.concat([{
    id: 'auto-invite-form', type: 'cta', mode: 'form',
    title: 'Оставьте заявку на курс',
    subtitle: 'На этот курс записывает методист. Оставьте контакты — с вами свяжутся и откроют доступ.',
  }]);
}

function autoBlocks(course) {
  const blocks = [{ id: 'auto-hero', type: 'hero', title: course.title, subtitle: course.summary || '' }];
  const descMd = course.description_md || course.description || '';
  if (descMd && descMd !== course.summary) {
    blocks.push({ id: 'auto-text', type: 'text', heading: 'О курсе', body_md: descMd });
  }
  blocks.push({ id: 'auto-program', type: 'program', heading: 'Программа курса', mode: 'auto' });
  blocks.push({ id: 'auto-pricing', type: 'pricing', heading: 'Стоимость' });
  blocks.push({
    id: 'auto-cta', type: 'cta',
    title: course.is_paid ? 'Готовы начать обучение?' : 'Запишитесь и начните учиться',
    subtitle: course.issues_certificate ? 'По завершении — именной сертификат.' : '',
  });
  return blocks;
}

function CourseLanding({ payload, mode = 'app' }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Открытая форма покупки: tariffId — тариф, выбранный кликом по карточке в
  // блоке «Тарифы» (AD-9); null = пришли из главной CTA и выбирают тариф в форме.
  const [buy, setBuy] = useState(null);   // { tariffId } | null

  const course = payload.course || {};
  const landing = payload.landing || {};
  const program = payload.program || [];
  const tariffs = payload.tariffs || [];
  const enr = payload.my_enrollment || null;
  const isPreview = mode === 'preview';
  const isAnon = mode === 'anon';

  const blocks = ensureLeadForm(
    (landing.enabled && Array.isArray(landing.blocks) && landing.blocks.length)
      ? landing.blocks
      : autoBlocks(course),
    course);

  // Прокрутка к форме заявки — единственное действие, доступное посетителю
  // invite-курса. Форму гарантирует ensureLeadForm выше, поэтому промахнуться
  // некуда; на всякий случай молчим, если её всё-таки нет (превью с чужой вёрсткой).
  const scrollToLeadForm = useCallback(() => {
    const el = document.querySelector('.cl-leadform');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const first = el.querySelector('input, textarea');
    if (first) first.focus({ preventScroll: true });
  }, []);

  // ── действие основной CTA (матрица режим × состояние) ──────────────────────
  const goPlayer = useCallback(() => navigate(`/learning/c/${course.id}`), [navigate, course.id]);

  const handleCta = useCallback(async () => {
    if (isPreview) return; // превью — кнопки инертны

    // записан
    if (enr && !isLandingAccessExpired(enr)) {
      // Завершён + есть сертификат В payload (бэк может обогатить — контракт его НЕ
      // гарантирует) → открыть сертификат в новой вкладке; иначе → плеер (там панель
      // сертификата и «Пройти заново»).
      if (enr.status === 'completed' && course.issues_certificate && enr.certificate && enr.certificate.public_id) {
        window.open(`/certificate/${enr.certificate.public_id}`, '_blank', 'noopener');
        return;
      }
      goPlayer();
      return;
    }

    // записан, но доступ истёк: продлеваем тем же путём, что и первичная покупка
    // (платный — форма с согласиями, бесплатный со сроком — повторная запись).
    if (enr) {
      if (course.is_paid) { setBuy({ tariffId: null }); return; }
      setBusy(true);
      try {
        await api.enroll(course.id);
        toast.show('Доступ к курсу возобновлён', 'success');
        goPlayer();
      } catch (e) {
        toast.show(academyErrorText(e, 'Не удалось продлить доступ'), 'danger');
        setBusy(false);
      }
      return;
    }

    // Курс «только по записи методистом»: самозапись закрыта гейтом бэка, и
    // единственный осмысленный шаг посетителя — заявка. Ведём к форме, а не в
    // отказ: раньше кнопка обещала запись, ловила invite_only и советовала
    // «оставьте заявку на странице курса», где формы могло не быть вовсе.
    if (isInviteOnly(course)) { scrollToLeadForm(); return; }

    // не записан + аноним → на регистрацию с возвратом на лендинг
    if (isAnon) {
      navigate(`/signup?next=${encodeURIComponent('/course/' + course.slug)}`);
      return;
    }

    // не записан + авторизован
    if (course.is_paid) {
      // платный → диалог покупки (тариф + согласия + телефон)
      setBuy({ tariffId: null });
      return;
    }
    // бесплатный → самозапись и в плеер
    setBusy(true);
    try {
      await api.enroll(course.id);
      toast.show('Вы записаны на курс', 'success');
      goPlayer();
    } catch (e) {
      toast.show(academyErrorText(e, 'Не удалось начать курс'), 'danger');
      setBusy(false);
    }
  }, [isPreview, enr, isAnon, course, navigate, goPlayer, toast, scrollToLeadForm]);

  // Фабрика CTA-кнопки — передаётся в hero/pricing/cta-блоки. Подпись считает
  // courseCtaLabel: состояние доступа ВЫИГРЫВАЕТ у статической подписи блока
  // (иначе на лендинге с заполненным «текстом кнопки» истёкший, незаписанный и
  // активный ученик видят одну и ту же кнопку, и «Продлить доступ» не появляется
  // никогда). variant primary; в preview — disabled (инертна).
  const cta = useCallback((label) => (
    <Button variant="primary" size="lg" loading={busy} disabled={isPreview}
      iconRight={enr && enr.status !== 'completed' && !isLandingAccessExpired(enr) ? 'arrow-right' : undefined}
      onClick={handleCta}>
      {courseCtaLabel({ course, enr, tariffs, blockLabel: label })}
    </Button>
  ), [busy, isPreview, enr, handleCta, course, tariffs]);

  // AD-9: покупка конкретного тарифа прямо из блока «Тарифы» — покупатель кликает
  // ту цену, которую видит, и она же приезжает в форму оплаты.
  const onBuyTariff = useCallback((tariffId) => {
    if (isPreview) return;
    if (isAnon) { navigate(`/signup?next=${encodeURIComponent('/course/' + course.slug)}`); return; }
    setBuy({ tariffId });
  }, [isPreview, isAnon, navigate, course.slug]);

  // Кнопки в карточках тарифов показываем, только когда покупка вообще уместна:
  // у активного ученика тариф уже оплачен, у истёкшего — это продление.
  const canBuyTariff = !!course.is_paid && (!enr || isLandingAccessExpired(enr));

  // slug/isPreview — для блоков с формой-заявкой (CTA mode='form'): submitLandingForm
  // шлёт заявку по slug; в preview форма инертна (как и остальные CTA).
  const ctx = { course, program, tariffs, cta, slug: course.slug, isPreview,
    onBuyTariff: canBuyTariff ? onBuyTariff : null };

  return (
    <div className={'cl-page cl-page--' + mode}>
      {isPreview && (
        <div className="cl-preview-banner">
          <Icon name="eye" size={15} />
          <span>Предпросмотр лендинга — кнопки действий неактивны.</span>
        </div>
      )}

      <div className="cl-blocks">
        {blocks.map((b, i) => {
          const el = renderBlock(b, ctx);
          if (!el) return null;
          return <React.Fragment key={(b && b.id) || i}>{el}</React.Fragment>;
        })}
      </div>

      {/* Аноним: подсказка про вход для тех, у кого уже есть аккаунт. */}
      {isAnon && (
        <div className="cl-signin-note">
          <span>Уже есть аккаунт?</span>
          <a href={`/signin?next=${encodeURIComponent('/course/' + course.slug)}`}>Войти</a>
        </div>
      )}

      {buy && (
        <BuyDialog courseId={course.id} courseTitle={course.title}
          coursePrice={course.price_rub} tariffs={tariffs} initialTariffId={buy.tariffId}
          onClose={() => setBuy(null)} />
      )}
    </div>
  );
}

export { CourseLanding, autoBlocks };
